import { DetectOverflowOptions, Placement, arrow, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import classNames from 'classnames';
import React, { ElementType, useEffect, useMemo, useState } from 'react';
import { useRef } from 'react';

type PropsOf<TTag extends React.ElementType> = TTag extends React.ElementType
    ? Omit<React.ComponentProps<TTag>, 'ref'>
    : never;

type Props<TTag extends ElementType> = {
    as?:
        | React.ExoticComponent<{
              children?: React.ReactNode | undefined;
          }>
        | undefined;
    panelClassname?: string;
    placement?: Placement;
    content?: React.ReactNode;
    disabled?: boolean;
    pt?: number;
    pl?: number;
    pr?: number;
    pb?: number;
} & PropsOf<TTag>;

export function PopoverTooltip<TTag extends ElementType>({
    as,
    children,
    placement: placementProp,
    panelClassname,
    content,
    disabled,
    pt = 10,
    pl = 10,
    pr = 10,
    pb = 10,
    ...props
}: React.PropsWithChildren<Props<TTag>>) {
    const [isOpen, setIsOpen] = useState(false);
    const [isTouchscreen, setIsTouchscreen] = useState(false);
    const arrowRef = useRef(null);

    const detectOverflow: DetectOverflowOptions = useMemo(
        () => ({
            padding: {
                top: pt,
                left: pl,
                right: pr,
                bottom: pb,
            },
        }),
        [pb, pl, pr, pt]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(pointer: coarse)');
        setIsTouchscreen(mediaQuery.matches);

        const handler = (e: MediaQueryListEvent) => setIsTouchscreen(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    // Automatically switch trigger based on screen size
    const activeTrigger = isTouchscreen ? 'click' : 'hover';

    const { refs, floatingStyles, middlewareData, placement } = useFloating({
        placement: placementProp,
        strategy: 'absolute',
        middleware: [offset(8), flip(detectOverflow), shift(detectOverflow), arrow({ element: arrowRef })],
    });

    // Event handlers active only during 'hover' mode
    const hoverHandlers =
        !disabled && activeTrigger === 'hover'
            ? {
                  onMouseEnter: () => setIsOpen(true),
                  onMouseLeave: () => setIsOpen(false),
              }
            : {};

    return (
        <Popover {...hoverHandlers}>
            {({ open }) => {
                // If trigger is 'click', use Headless UI's native state ('open').
                // If trigger is 'hover', use our manual 'isOpen' state.
                const showPopover = disabled ? false : activeTrigger === 'hover' ? isOpen : open;

                return (
                    <>
                        <PopoverButton as={as} ref={refs.setPositionReference} disabled={disabled} {...props}>
                            {children}
                        </PopoverButton>
                        <Transition
                            show={showPopover}
                            enter="transition-opacity"
                            leave="transition-opacity"
                            enterFrom="opacity-0"
                            leaveTo="opacity-0"
                        >
                            <PopoverPanel
                                className="absolute z-10 w-64 drop-shadow"
                                ref={refs.setFloating}
                                style={floatingStyles}
                            >
                                {/* arrow */}
                                <div
                                    className={classNames('absolute size-2 rotate-45 bg-white', {
                                        '-left-1': placement === 'right',
                                        '-right-1': placement === 'left',
                                        '-top-1': placement === 'bottom',
                                        '-bottom-1': placement === 'top',
                                    })}
                                    ref={arrowRef}
                                    style={{
                                        left: middlewareData.arrow?.x,
                                        top: middlewareData.arrow?.y,
                                    }}
                                ></div>
                                <div className={classNames('rounded border bg-white p-3', panelClassname)}>
                                    {content}
                                </div>
                            </PopoverPanel>
                        </Transition>
                    </>
                );
            }}
        </Popover>
    );
}
